import { PaletteOptions } from '@mui/material/styles';

const RGB_HEX = /^#?(?:([\da-f]{3})[\da-f]?|([\da-f]{6})(?:[\da-f]{2})?)$/i;
/**
 * Parses a hex color string into its RGB components.
 *
 * @param str - Hex color string (e.g. "#fff", "#ffffff", "ffffff")
 * @returns Tuple of [r, g, b] or undefined if the input is invalid.
 */
const hex2RGB = (str: string): [number, number, number] | undefined => {
  const [, short, long] = String(str).match(RGB_HEX) || [];

  if (long) {
    const value = Number.parseInt(long, 16);
    return [value >> 16, (value >> 8) & 0xff, value & 0xff];
  } else if (short) {
    return Array.from(short, (s) => Number.parseInt(s, 16)).map((n) => (n << 4) | n) as [
      number,
      number,
      number,
    ];
  }
};

/**
 * Retrieves an RGBA color string from a hex value and transparency level.
 *
 * @param hex - Hex color string (e.g. "#fff", "#ffffff")
 * @param transparency - Alpha value between 0 and 1
 * @returns RGBA color string or the original hex if parsing fails.
 */
const getRgba = (hex: string, transparency: number) => {
  // If parameters are not defined, return original hex
  if (!hex || transparency == null) {
    return hex;
  }

  const rgb = hex2RGB(hex);
  if (!rgb) {
    return hex;
  }

  const [r, g, b] = rgb;

  return `rgba(${r}, ${g}, ${b}, ${transparency})`;
};

// Shared palette colors used in both light and dark modes
const sharedPalette = {
  getRgba,
  custom: {
    red: {
      main: '#B72F12',
      light: '#E62F09',
    },
    grey: {
      dark: '#333333',
      main: '#C8C8C8',
      light: '#EBEBEB',
    },
    green: {
      main: '#26A69A',
      light: '#00E676',
      faded: '#35BE95',
    },
    blue: {
      main: '#3B88FD',
      light: '#558ac5',
    },
    blackOpaque: 'rgba(0, 0, 0, 0.08)',
    yellow: '#ffff29',
    gainsboro: '#E0E0E0',
  },
  brands: {
    elevator_red: '#F82600',
    youtube_red: '#FF0000',
    soundcloud_orange: '#FE5000',
    twitter_blue: '#1DA1F2',
    reddit_orange: '#FF5700',
  },
  submissionStatuses: {
    review: '#FFB300',
    approval: '#4DD0E1',
    completed: '#009688',
    flagged: '#FFB74D',
    rejected: '#F44336',
    approved: '#1E88E5',
  },
};

// Light mode palette
export const lightPalette: PaletteOptions = {
  ...sharedPalette,
  mode: 'light',
  primary: {
    main: '#262626',
    light: '#414141',
    dark: '#000000',
    contrastText: '#FFFFFF',
  },
  /*
  secondary: {
    main: '#F82600',
    light: '#FF3F3F',
    dark: '',
    contrastText: '#FFFFFF',
  },*/
  secondary: {
    main: '#BDBDBD',
    light: '#CCCCCC',
    dark: '#757575',
    contrastText: '#121212',
  },
  background: {
    default: '#F6F6F6',
  },
  action: {
    disabled: 'rgba(0, 0, 0, 0.26)', // Darker/more visible disabled text (default 0.26)
    disabledBackground: 'rgba(0, 0, 0, 0.09)', // More visible disabled background (default 0.12)
    selected: 'rgba(0, 0, 0, 0.08)', // Selected/highlighted background in light mode
  },
};

// Dark mode palette
export const darkPalette: PaletteOptions = {
  ...sharedPalette,
  mode: 'dark',
  primary: {
    main: '#E0E0E0',
    light: '#EEEEEE',
    dark: '#BDBDBD',
    contrastText: '#121212',
  },
  secondary: {
    main: '#757575',
    light: '#8E8E8E',
    dark: '#616161',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#101010',
  },
  action: {
    disabled: 'rgba(255, 255, 255, 0.26)', // Lighter/more visible disabled text in dark mode (default 0.3)
    disabledBackground: 'rgba(255, 255, 255, 0.09)', // More visible disabled background (default 0.12)
    selected: 'rgba(255, 255, 255, 0.12)', // Selected/highlighted background in dark mode - slightly more visible for better contrast
  },
};

// Default export for backwards compatibility
export const palette = lightPalette;
export { palette as default };
